import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/apiServices';
import { useFeedback } from '../../contexts/FeedbackContext';
import { ArrowLeftIcon, MailIcon } from '../../components/common/Icons';
import { AuthField } from './LoginPage';
import { requiredFieldMessage } from '../../utils/validationMessages';

const ForgotPasswordPage = () => {
  const { notify } = useFeedback();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim()) {
      const message = requiredFieldMessage(t, t('auth.email'));
      setEmailError(message);
      notify({ variant: 'warning', title: t('feedback.invalidEmailTitle'), message });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('feedback.invalidEmailMessage'));
      notify({ variant: 'warning', title: t('feedback.invalidEmailTitle'), message: t('feedback.invalidEmailMessage') });
      return;
    }
    setEmailError('');

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(email);
      setResetUrl(response.data?.resetUrl || '');
      setSent(true);
      notify({ variant: 'success', title: t('feedback.recoverySentTitle'), message: t('feedback.recoverySentMessage') });
    } catch {
      notify({ variant: 'danger', title: t('feedback.recoveryErrorTitle'), message: t('feedback.recoveryErrorMessage') });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-soft">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <MailIcon className="h-8 w-8" />
        </div>
        <h1 className="mt-6 text-2xl font-bold text-slate-900">{t('auth.emailSent')}</h1>
        <p className="mt-3 text-slate-600">{t('auth.emailSentText')}</p>
        {resetUrl && (
          <a href={resetUrl} className="mt-4 block break-all rounded-lg bg-slate-50 p-3 text-sm font-semibold text-sky-600">
            {resetUrl}
          </a>
        )}
        <Link to="/login" className="mt-7 inline-flex items-center gap-2 font-bold text-sky-500 hover:text-sky-600">
          <ArrowLeftIcon className="h-4 w-4" />
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-soft">
      <h1 className="text-center text-2xl font-bold text-slate-900">{t('auth.forgotTitle')}</h1>
      <p className="mt-3 text-center text-slate-600">{t('auth.forgotSubtitle')}</p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        <AuthField
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            setEmailError('');
          }}
          placeholder="seu@email.com"
          icon={<MailIcon className="h-5 w-5" />}
          autoComplete="email"
          error={emailError}
        />
        <button
          className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-sky-500 px-5 font-bold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
          type="submit"
          disabled={loading}
        >
          {loading ? t('common.processing') : t('auth.sendRecovery')}
        </button>
      </form>

      <Link to="/login" className="mt-7 flex items-center justify-center gap-2 font-bold text-sky-500 hover:text-sky-600">
        <ArrowLeftIcon className="h-4 w-4" />
        {t('auth.backToLogin')}
      </Link>
    </div>
  );
};

export default ForgotPasswordPage;
